import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Box, Button, Card, CardContent, Chip,
  Sheet, Stack, Typography, CircularProgress,
  Switch, Divider, Snackbar,
} from "@mui/joy";

import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneAndroidRoundedIcon from "@mui/icons-material/PhoneAndroidRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import AddAlertRoundedIcon from "@mui/icons-material/AddAlertRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";

import { LayoutContext } from "../../context";
import {
  APIClientGetNotificationSettings,
  APIClientUpdateNotificationSettings,
  APIClientPushTest,
} from "../../api";


// ── Конфигурация настроек ─────────────────────────────────────────
const NOTIFICATION_GROUPS = [
  {
    title: "Заявки",
    icon: <AssignmentTurnedInRoundedIcon sx={{ fontSize: 20 }} />,
    items: [
      {
        pushKey: "application_created",
        emailKey: "email_application_created",
        label: "Заявка создана",
        description: "Подтверждение создания новой заявки",
        icon: <AddAlertRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        pushKey: "status_changed",
        emailKey: "email_status_changed",
        label: "Смена статуса",
        description: "Уведомление при изменении статуса заявки",
        icon: <SyncRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        pushKey: "application_completed",
        emailKey: "email_application_completed",
        label: "Заявка завершена",
        description: "Уведомление о завершении заявки",
        icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
  {
    title: "Замеры",
    icon: <CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />,
    items: [
      {
        pushKey: "event_scheduled",
        emailKey: "email_event_scheduled",
        label: "Замер назначен",
        description: "Когда на вашу заявку назначен замер",
        icon: <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        pushKey: "event_rescheduled",
        emailKey: "email_event_rescheduled",
        label: "Замер перенесён",
        description: "При изменении даты или времени замера",
        icon: <EventRepeatRoundedIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
  {
    title: "Прочее",
    icon: <ChatRoundedIcon sx={{ fontSize: 20 }} />,
    items: [
      {
        pushKey: "new_message",
        emailKey: "email_new_message",
        label: "Новое сообщение",
        description: "Уведомления о сообщениях в чате",
        icon: <ChatRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        pushKey: "child_account_added",
        emailKey: "email_child_account_added",
        label: "Новый сотрудник",
        description: "Когда добавлен новый сотрудник компании",
        icon: <PersonAddRoundedIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
  {
    title: "Платежи",
    icon: <CreditCardRoundedIcon sx={{ fontSize: 20 }} />,
    items: [
      {
        pushKey: "payment_required",
        emailKey: "email_payment_required",
        label: "Требуется оплата",
        description: "Когда по заявке нужно оплатить услугу",
        icon: <PaymentRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        pushKey: "payment_completed",
        emailKey: "email_payment_completed",
        label: "Оплата прошла",
        description: "Подтверждение успешной оплаты",
        icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />,
      },
    ],
  },
];


// ── Компонент одной настройки ──────────────────────────────────────
function SettingRow({ item, settings, onToggle, saving }) {
  const pushEnabled = settings?.[item.pushKey] ?? true;
  const emailEnabled = item.emailKey ? (settings?.[item.emailKey] ?? false) : null;

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        {/* Иконка */}
        <Sheet
          variant="soft" color="primary"
          sx={{
            width: 36, height: 36, borderRadius: "10px",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, mt: 0.3,
          }}
        >
          {item.icon}
        </Sheet>

        {/* Текст + переключатели */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-sm" fontWeight={600}>
            {item.label}
          </Typography>
          <Typography level="body-xs" color="neutral" sx={{ mt: 0.2 }}>
            {item.description}
          </Typography>

          {/* Тогглы */}
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PhoneAndroidRoundedIcon sx={{ fontSize: 15, color: "text.tertiary" }} />
              <Typography level="body-xs" color="neutral" fontWeight={500}>
                Push
              </Typography>
              <Switch
                size="sm"
                checked={pushEnabled}
                disabled={saving}
                onChange={() => onToggle(item.pushKey, !pushEnabled)}
                sx={{
                  "--Switch-trackWidth": "36px",
                  "--Switch-trackHeight": "20px",
                }}
              />
            </Stack>

            {item.emailKey && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailRoundedIcon sx={{ fontSize: 15, color: "text.tertiary" }} />
                <Typography level="body-xs" color="neutral" fontWeight={500}>
                  Email
                </Typography>
                <Switch
                  size="sm"
                  checked={emailEnabled}
                  disabled={saving}
                  onChange={() => onToggle(item.emailKey, !emailEnabled)}
                  sx={{
                    "--Switch-trackWidth": "36px",
                    "--Switch-trackHeight": "20px",
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}


// ── Push-статус баннер ─────────────────────────────────────────────
function PushStatusBanner({ pushPermission, onRequestPush, pwaStatus, pushState }) {
  // «Включены» — только если подписка реально существует на этом устройстве.
  // Разрешение браузера остаётся granted и после очистки данных сайта,
  // когда уведомления уже не доходят.
  const reallyOn = pushPermission === "granted" && pushState?.subscribed !== false;

  if (pushPermission === "granted" && !reallyOn) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card variant="soft" color="warning" sx={{ borderRadius: "16px", p: 0 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <WarningRoundedIcon sx={{ fontSize: 22 }} />
                <Box>
                  <Typography level="title-sm" fontWeight={700}>
                    Уведомления разрешены, но не подключены
                  </Typography>
                  <Typography level="body-xs">
                    Подписка этого устройства потерялась — уведомления сейчас не приходят
                  </Typography>
                </Box>
              </Stack>
              <Button size="sm" onClick={onRequestPush} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                Подключить
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (reallyOn) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card
          variant="soft" color="success"
          sx={{ borderRadius: "16px", p: 0 }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <NotificationsActiveRoundedIcon sx={{ fontSize: 22 }} />
              <Box>
                <Typography level="title-sm" fontWeight={700}>
                  Push-уведомления включены
                </Typography>
                <Typography level="body-xs">
                  Вы будете получать мгновенные уведомления на это устройство
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (pushPermission === "denied") {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card
          variant="soft" color="warning"
          sx={{ borderRadius: "16px", p: 0 }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <WarningRoundedIcon sx={{ fontSize: 22 }} />
              <Box>
                <Typography level="title-sm" fontWeight={700}>
                  Push-уведомления заблокированы
                </Typography>
                <Typography level="body-xs">
                  Разрешите уведомления в настройках браузера, чтобы получать мгновенные оповещения
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // default — можно запросить
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
      <Card
        variant="outlined"
        sx={{ borderRadius: "16px", p: 0 }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Sheet
                variant="soft" color="primary"
                sx={{
                  width: 44, height: 44, borderRadius: "14px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <NotificationsRoundedIcon sx={{ fontSize: 24 }} />
              </Sheet>
              <Box>
                <Typography level="title-sm" fontWeight={700}>
                  Включите Push-уведомления
                </Typography>
                <Typography level="body-xs" color="neutral">
                  Мгновенные уведомления о статусе ваших заявок прямо в браузере
                </Typography>
              </Box>
            </Stack>
            <Button
              size="sm"
              onClick={onRequestPush}
              sx={{
                fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                flexShrink: 0,
              }}
            >
              Включить
            </Button>
          </Stack>

          {/* iOS подсказка */}
          {pwaStatus.platform === "ios" && !pwaStatus.isInstalled && (
            <Sheet
              variant="soft" color="neutral"
              sx={{ borderRadius: "10px", p: 1.5, mt: 1.5 }}
            >
              <Typography level="body-xs" color="neutral">
                📱 На iPhone сначала добавьте сайт на главный экран:
                нажмите «Поделиться» → «На экран Домой»
              </Typography>
            </Sheet>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}


// ── Страница «Уведомления» ────────────────────────────────────────
export default function NotificationsPage() {
  const {
    account, pushPermission,
    pushState, refreshPushState,
    subscribeUserToPush, pwaStatus,
  } = useContext(LayoutContext);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  // ── Загрузка настроек ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    APIClientGetNotificationSettings().then((data) => {
      if (cancelled) return;
      if (data && !data.status) {
        setSettings(data);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // ── Переключение настройки ──────────────────────────────────────
  const handleToggle = async (key, value) => {
    // Оптимистичное обновление
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaving(true);

    const result = await APIClientUpdateNotificationSettings({ [key]: value });

    if (result?.status === "error") {
      // Откатываем
      setSettings(prev => ({ ...prev, [key]: !value }));
      setSnackbar({ open: true, message: result.detail || "Ошибка сохранения", color: "danger" });
    } else {
      // Обновляем из ответа сервера
      if (result && !result.status) {
        setSettings(result);
      }
    }
    setSaving(false);
  };

  // ── Запрос push-разрешения ──────────────────────────────────────
  const handleRequestPush = async () => {
    const result = await subscribeUserToPush();
    // Перечитываем реальное состояние, иначе баннер останется прежним
    await refreshPushState?.();

    const messages = {
      success: "Push-уведомления включены!",
      permission_denied: "Вы заблокировали уведомления. Разрешите их в настройках браузера.",
      permission_dismissed: "Вы отклонили запрос. Попробуйте ещё раз.",
      ios_need_install: "Добавьте сайт на главный экран, чтобы получать уведомления.",
      not_supported: "Ваш браузер не поддерживает push-уведомления.",
    };

    setSnackbar({
      open: true,
      message: messages[result?.type] || "Что-то пошло не так",
      color: result?.type === "success" ? "success" : "warning",
    });
  };

  // ── Тестовый push ───────────────────────────────────────────────
  const handleTestPush = async () => {
    const result = await APIClientPushTest();
    setSnackbar({
      open: true,
      message: result?.status === "sent"
        ? "Тестовое уведомление отправлено!"
        : "Не удалось отправить тестовое уведомление",
      color: result?.status === "sent" ? "success" : "warning",
    });
  };

  // ── Подсчёт включённых ─────────────────────────────────────────
  const countEnabled = () => {
    if (!settings) return { push: 0, email: 0 };
    let push = 0, email = 0;
    for (const group of NOTIFICATION_GROUPS) {
      for (const item of group.items) {
        if (settings[item.pushKey]) push++;
        if (item.emailKey && settings[item.emailKey]) email++;
      }
    }
    return { push, email };
  };

  const counts = countEnabled();

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>

      {/* Заголовок */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <Box>
            <Typography level="h2" sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}>
              Уведомления 🔔
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
              Настройте способы получения уведомлений
            </Typography>
          </Box>

          {pushPermission === "granted" && (
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="outlined"
                color="neutral"
                size="sm"
                onClick={handleTestPush}
                startDecorator={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{ fontWeight: 600, borderRadius: "10px" }}
              >
                Тестовое уведомление
              </Button>
            </motion.div>
          )}
        </Stack>
      </motion.div>

      {/* Статистика */}
      {settings && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
            gap: 1.5, mb: 3,
          }}
        >
          {[
            {
              label: "Push включено",
              value: counts.push,
              color: "primary",
              icon: <PhoneAndroidRoundedIcon sx={{ fontSize: 14 }} />,
            },
            {
              label: "Email включено",
              value: counts.email,
              color: "primary",
              icon: <EmailRoundedIcon sx={{ fontSize: 14 }} />,
            },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
            >
              <Card variant="outlined" sx={{ borderRadius: "16px", p: 0 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    level="h2"
                    sx={{
                      fontWeight: 800, lineHeight: 1,
                      letterSpacing: "-1px",
                      color: `${s.color}.600`,
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    {s.icon}
                    <Typography level="body-xs" color="neutral" fontWeight={600}>
                      {s.label}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      )}

      {/* Push-статус баннер */}
      <Box sx={{ mb: 2.5 }}>
        <PushStatusBanner
          pushPermission={pushPermission}
          onRequestPush={handleRequestPush}
          pwaStatus={pwaStatus}
          pushState={pushState}
        />
      </Box>

      {/* Email-статус баннер */}
      {account && !account.email && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card
            variant="soft" color="warning"
            sx={{ borderRadius: "16px", p: 0, mb: 2.5 }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <EmailRoundedIcon sx={{ fontSize: 22 }} />
                <Box>
                  <Typography level="title-sm" fontWeight={700}>
                    Укажите email в профиле
                  </Typography>
                  <Typography level="body-xs">
                    Чтобы получать email-уведомления, добавьте адрес электронной почты в настройках профиля
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Группы настроек */}
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" color="neutral" sx={{ mt: 1.5 }}>
            Загружаем настройки...
          </Typography>
        </Stack>
      ) : !settings ? (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Sheet
            variant="soft" color="neutral"
            sx={{
              borderRadius: "20px", p: 6,
              textAlign: "center",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            }}
          >
            <Typography level="title-md" fontWeight={700}>
              Не удалось загрузить настройки
            </Typography>
            <Typography level="body-sm" color="neutral">
              Попробуйте обновить страницу
            </Typography>
          </Sheet>
        </motion.div>
      ) : (
        <Stack spacing={2}>
          {NOTIFICATION_GROUPS.map((group, gi) => (
            <motion.div
              key={group.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + gi * 0.08 }}
            >
              <Card
                variant="outlined"
                sx={{ borderRadius: "18px", p: 0, overflow: "hidden" }}
              >
                {/* Цветная полоска */}
                <Box
                  sx={{
                    height: 3,
                    background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                  }}
                />

                <CardContent sx={{ p: 2.5 }}>
                  {/* Заголовок группы */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Sheet
                      variant="soft" color="primary"
                      sx={{
                        width: 32, height: 32, borderRadius: "10px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {group.icon}
                    </Sheet>
                    <Typography level="title-md" fontWeight={700}>
                      {group.title}
                    </Typography>
                  </Stack>

                  {/* Настройки */}
                  {group.items.map((item, ii) => (
                    <Box key={item.pushKey}>
                      {ii > 0 && <Divider sx={{ my: 0.5 }} />}
                      <SettingRow
                        item={item}
                        settings={settings}
                        onToggle={handleToggle}
                        saving={saving}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Stack>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        autoHideDuration={4000}
        color={snackbar.color}
        variant="soft"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ borderRadius: "12px" }}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
}